/* eslint-disable complexity */
import { capitalize, trim } from 'lodash';
import React, { useEffect, useState } from 'react';
import Dialog from 'carbon-react/lib/components/dialog';
import Button from 'carbon-react/lib/components/button';
import Form from 'carbon-react/lib/components/form';
import Textbox from 'carbon-react/lib/components/textbox';
import Box from 'carbon-react/lib/components/box';

import { FormattedMessage, IntlShape, useIntl } from '@futrli/localisation';
import { createLengthValidator } from '@futrli/shared-services';
import { UILoaderSpinner } from '@futrli/ui-components';

import { AssumptionTypesUnion, ASSUMPTION_TYPE_NAMES } from '../../../../../core/constants';

interface OwnProps {
  onClose: () => void;
  onSuccess: (name: string) => void;
  type: AssumptionTypesUnion;
  name?: string;
  isRenaming?: boolean;
}

type Props = OwnProps;

const MAX_NAME_LENGTH = 100;
const nameValidator = createLengthValidator(MAX_NAME_LENGTH);

export const getMaxLimitMessage = (intl: IntlShape) =>
  intl.formatMessage({ id: 'validation.max_hundred', defaultMessage: 'We only allow up to 100 characters.' });

export const getEmptyFieldMessage = (intl: IntlShape) =>
  intl.formatMessage({ id: 'common.please_enter_name_period', defaultMessage: 'Please enter a name.' });

export const ModalName = ({ onSuccess, onClose, name: nameProp, type, isRenaming }: Props) => {
  const intl = useIntl();

  const [name, setName] = useState(nameProp || '');
  const [valid, setValid] = useState(false);
  const typeName = ASSUMPTION_TYPE_NAMES(intl)[type];

  useEffect(() => setValid(trim(name) !== '' && nameValidator(name)), [name]);

  const handleSave = (): void => {
    const trimmed = trim(name);
    onSuccess(trimmed);
  };

  const handleEnterKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' && valid) {
      handleSave();
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const current = event.currentTarget.value;

    setName(current);
  };

  const getError = (name: string) => (trim(name) === '' ? getEmptyFieldMessage(intl) : getMaxLimitMessage(intl));

  return (
    <Dialog
      open={true}
      onCancel={onClose}
      title={
        nameProp
          ? intl.formatMessage(
              { id: 'predict.rename_prediction_heading', defaultMessage: 'Rename {predictionType} prediction' },
              {
                predictionType: capitalize(typeName)
              }
            )
          : intl.formatMessage(
              { id: 'predict.name_prediction_heading', defaultMessage: 'Name your {predictionType} prediction' },
              {
                predictionType: capitalize(typeName)
              }
            )
      }
      size="medium"
    >
      <Form
        stickyFooter
        leftSideButtons={
          <Button onClick={onClose} buttonType="secondary">
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
        }
        saveButton={
          <Button onClick={handleSave} disabled={!valid} buttonType="primary">
            {nameProp ? (
              <FormattedMessage id="common.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="common.next" defaultMessage="Next" />
            )}
          </Button>
        }
        onSubmit={e => e.preventDefault()}
      >
        {isRenaming ? (
          <Box p={3}>
            <UILoaderSpinner />
          </Box>
        ) : (
          <Textbox
            size="large"
            autoFocus={true}
            onChange={handleOnChange}
            id="name"
            value={name}
            error={!valid && name !== '' ? getError(name) : undefined}
            placeholder={
              nameProp
                ? intl.formatMessage({ id: 'predict.rename_placeholder', defaultMessage: 'Prediction Name' })
                : intl.formatMessage({ id: 'predict.name_placeholder', defaultMessage: 'e.g. Coffee sales' })
            }
            autoComplete="off"
            onKeyDown={handleEnterKeyPress}
          />
        )}
      </Form>
    </Dialog>
  );
};
